/**
 * Imperative three.js WebGPU scene lifecycle for the World viewer's 3D pane
 * (`viewers/WorldViewer.tsx`) — mirrors `three-model-scene.ts`'s split
 * (component stays JSX-and-wiring) and reuses the same ground/sky dressing
 * `/fly` uses (`flight/world.ts`) and the same instancing infra `/fly`'s
 * region loader uses (`three/world-instancer.ts`), per this lane's brief:
 * one WebGPU scene infrastructure, not a second copy of it.
 */

import * as THREE from "three/webgpu";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { createGround, createSky, GROUND_HALF_EXTENT_M } from "../flight/world.ts";
import { disposeTree } from "../three/dispose-tree.ts";
import { disableWebGlFallback, watchDeviceLoss } from "../three/webgpu-support.ts";
import { LatestRequestGuard } from "../three/latest-request-guard.ts";
import { LruCache } from "../three/lru-cache.ts";
import { buildWorldInstances, type InstancerPlacement } from "../three/world-instancer.ts";
import { placementWorldTransform, type PlacementOrigin } from "../world/placement-transform.ts";
import { wldUnitsToMetres } from "../world/wld-units.ts";
import type { WorldBoundsBody } from "../world/region-loader.ts";

export type WorldSceneStatus = "initializing" | "ready" | "error";

export interface WorldSceneCallbacks {
  readonly onStatus: (status: WorldSceneStatus, message?: string) => void;
}

export interface WorldSceneRegion {
  /** Stable identity for this region (the caller passes its baked source path) — the key `regionCache` is keyed on. */
  readonly key: string;
  readonly bounds: WorldBoundsBody;
  readonly placements: readonly InstancerPlacement[];
}

export interface WorldScene {
  /** Replaces the shown region with `region`, disposing the previous one, and frames the camera on it. */
  setRegion(region: WorldSceneRegion): Promise<void>;
  /** Moves the orbit target/camera to one placement (region-relative `x`/`y`, `.WLD` units); `undefined` clears the focus. */
  focusPlacement(placement: { readonly x: number; readonly y: number; readonly rotationDegrees: number } | undefined): void;
  readonly drawCallCount: () => number;
  dispose(): void;
}

const BACKGROUND_COLOR = 0x0a0d08;
const FRAME_DISTANCE_FACTOR = 1.6;
const FOCUS_DISTANCE_M = 120;
const GROUND_MARGIN_FACTOR = 1.2;
/**
 * The ground dressing is built once at unit size and *scaled* per region
 * rather than rebuilt at the region's size — see `buildDressing`. `0.5`
 * makes `createGround`'s plane/grid span -0.5..+0.5, so a scale of
 * `2 * halfExtentM` reproduces `createGround(halfExtentM)` exactly.
 */
const GROUND_UNIT_HALF_EXTENT = 0.5;
/**
 * How many built region groups to keep resident.
 *
 * The baked catalogue has six world regions (`data/baked/manifest.json`), so
 * eight keeps every one of them resident with headroom while still *bounding*
 * the cache for a future, larger catalogue. Each retained region is small —
 * one region's distinct baked models total a few hundred KB of JSON, well
 * under a megabyte of GPU buffers once expanded — which is what makes
 * retaining them obviously cheaper than rebuilding them (see `regionCache`).
 */
const REGION_CACHE_CAPACITY = 8;

/** One region's built scenery, as `regionCache` holds it. */
interface CachedRegion {
  readonly group: THREE.Group;
  readonly drawCallCount: number;
}

interface SceneState {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGPURenderer;
  readonly controls: OrbitControls;
  readonly container: HTMLDivElement;
  /** Sky + ground, owned by the scene rather than by whichever region is shown — see `buildDressing`. */
  readonly ground: THREE.Group;
  regionGroup: THREE.Group | undefined;
  /**
   * Built region content, kept alive between visits instead of rebuilt.
   *
   * This is the fix for the World tab's unbounded growth under region
   * switching, and the reason is specific to how `WebGPURenderer` caches
   * pipelines: `RenderObject.getMaterialCacheKey()` folds `object.uuid` into
   * the key **for every `InstancedMesh`** (three r185, with an upstream TODO
   * on the line), so a pipeline is per-instanced-mesh-*instance*, not per
   * material. Rebuilding a region therefore hands Dawn ~50 brand-new
   * pipelines to compile — one per distinct object ref — whose native memory
   * the JS heap never accounts for; measured at +225 MB of renderer RSS over
   * 300 switches with the JS heap flat at 12-44 MB. Nothing about material or
   * geometry reuse can avoid that: only keeping the *same mesh objects* can.
   * Evicted entries are disposed through `disposeTree`.
   */
  readonly regionCache: LruCache<string, CachedRegion>;
  origin: PlacementOrigin;
  disposed: boolean;
  pendingInit: Promise<void>;
  /** Draw calls added by the most recently *applied* (non-stale) region — see `regionRequestGuard`. */
  drawCallCount: number;
  /**
   * `setRegion` is now called repeatedly against one long-lived scene
   * (`world-three-pane.tsx` no longer recreates the scene per region), and
   * `buildWorldInstances` awaits a network fetch per distinct object ref,
   * so two calls can be in flight at once if a caller selects a new region
   * before the previous one finished loading — see `LatestRequestGuard`'s
   * doc comment for why that's a real leak without this guard.
   */
  readonly regionRequestGuard: LatestRequestGuard;
}

/**
 * Sky + ground, built **once per scene** and never rebuilt.
 *
 * These used to be rebuilt inside every region's group, so the sky, the
 * ground plane and the survey grid were three more meshes (and three more
 * compiled pipelines) thrown away and remade on every region switch, for
 * content that is identical at every region bar the ground's extent. Keeping
 * them is the same argument `regionCache` makes, minus the cache: the meshes
 * are never removed from the scene, so the renderer keeps reusing their
 * render objects, bindings and pipelines frame after frame.
 *
 * The ground is size-independent because it is built at unit extent and
 * scaled in X/Z only (`applyGroundExtent`) — Y is deliberately left at 1 so
 * the plane's 8 cm sub-datum offset and the grid's 20 cm lift, both of which
 * `flight/world.ts` documents as depth-fighting fixes, keep their absolute
 * values at every region size.
 */
function buildDressing(): { dressing: THREE.Group; ground: THREE.Group } {
  const dressing = new THREE.Group();
  dressing.add(createSky());
  const ground = createGround(GROUND_UNIT_HALF_EXTENT);
  // Until the first region lands there is no extent to fit, so start at the
  // same ground size `/fly` uses rather than at the bare unit size.
  ground.scale.set(GROUND_HALF_EXTENT_M * 2, 1, GROUND_HALF_EXTENT_M * 2);
  dressing.add(ground);
  return { dressing, ground };
}

function applyGroundExtent(state: SceneState, halfExtentM: number): void {
  state.ground.scale.set(halfExtentM * 2, 1, halfExtentM * 2);
}

function buildSceneObjects(container: HTMLDivElement): {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGPURenderer;
  controls: OrbitControls;
  ground: THREE.Group;
} {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(BACKGROUND_COLOR);
  const { dressing, ground } = buildDressing();
  scene.add(dressing);

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200000);
  camera.position.set(500, 400, 500);

  const renderer = new THREE.WebGPURenderer({ antialias: true, reversedDepthBuffer: true });
  disableWebGlFallback(renderer);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  return { scene, camera, renderer, controls, ground };
}

function resize(state: SceneState): void {
  const width = state.container.clientWidth || 1;
  const height = state.container.clientHeight || 1;
  state.renderer.setSize(width, height, false);
  state.camera.aspect = width / height;
  state.camera.updateProjectionMatrix();
}

function watchInit(state: SceneState, callbacks: WorldSceneCallbacks): Promise<void> {
  return state.renderer
    .init()
    .then(() => {
      if (state.disposed) return;
      resize(state);
      state.renderer.setAnimationLoop(() => {
        state.controls.update();
        state.renderer.render(state.scene, state.camera);
      });
      callbacks.onStatus("ready");
      watchDeviceLoss(state.renderer, (info) => {
        if (state.disposed) return;
        callbacks.onStatus("error", `GPU device lost (${info.reason}): ${info.message}`);
      });
    })
    .catch((error: unknown) => {
      if (state.disposed) return;
      callbacks.onStatus("error", error instanceof Error ? error.message : String(error));
    });
}

/**
 * Detaches the shown region from the scene **without disposing it** — it
 * stays owned by `regionCache`, which is the whole point (see that field).
 * Disposal happens on cache eviction and on `dispose()`.
 */
function detachRegion(state: SceneState): void {
  if (!state.regionGroup) return;
  state.scene.remove(state.regionGroup);
  state.regionGroup = undefined;
}

function frameOnBounds(state: SceneState, halfWidthM: number, halfHeightM: number): void {
  const radius = Math.max(Math.hypot(halfWidthM, halfHeightM), 10);
  const distance = radius * FRAME_DISTANCE_FACTOR;
  state.camera.position.set(distance * 0.6, distance * 0.55, distance * 0.6);
  state.camera.near = Math.max(radius / 500, 0.1);
  state.camera.far = Math.max(distance * 20, 2000);
  state.camera.updateProjectionMatrix();
  state.controls.target.set(0, 0, 0);
  state.controls.update();
}

async function buildRegionGroup(region: WorldSceneRegion, origin: PlacementOrigin): Promise<CachedRegion> {
  const group = new THREE.Group();
  const instances = await buildWorldInstances(region.placements, origin);
  group.add(instances.group);
  return { group, drawCallCount: instances.drawCallCount };
}

async function applyRegion(state: SceneState, region: WorldSceneRegion): Promise<void> {
  const requestId = state.regionRequestGuard.begin();
  detachRegion(state);

  const centerX = (region.bounds.minX + region.bounds.maxX) / 2;
  const centerY = (region.bounds.minY + region.bounds.maxY) / 2;
  const origin = { x: wldUnitsToMetres(centerX), z: wldUnitsToMetres(centerY) };

  const halfWidthM = wldUnitsToMetres(region.bounds.maxX - region.bounds.minX) / 2;
  const halfHeightM = wldUnitsToMetres(region.bounds.maxY - region.bounds.minY) / 2;
  const groundHalfExtentM = Math.max(halfWidthM, halfHeightM, 100) * GROUND_MARGIN_FACTOR;

  // A cache hit costs no fetch, no geometry build and — the expensive part —
  // no pipeline compilation, because it is the *same mesh objects* going back
  // into the scene. It is also synchronous, so the guard below can only ever
  // fire for a freshly-built region.
  const cached = state.regionCache.get(region.key);
  const built = cached ?? (await buildRegionGroup(region, origin));

  // A newer `applyRegion` call (or a `dispose()`) started while this one was
  // awaiting the fetch above — this result is stale, discard it rather than
  // adding a second, orphaned group next to whatever the newer call applied.
  if (state.disposed || !state.regionRequestGuard.isCurrent(requestId)) {
    // Only a *freshly built* group is ours to throw away; a cached one is
    // still owned by `regionCache` and whatever call did win may be showing it.
    if (!cached) disposeTree(built.group);
    return;
  }

  if (!cached) state.regionCache.set(region.key, built);

  applyGroundExtent(state, groundHalfExtentM);
  state.origin = origin;
  state.scene.add(built.group);
  state.regionGroup = built.group;
  state.drawCallCount = built.drawCallCount;
  frameOnBounds(state, halfWidthM, halfHeightM);
}

/**
 * Creates and owns one three.js WebGPU scene attached to `container`.
 * Assumes `navigator.gpu` exists (the caller feature-detects — see
 * `ModelViewer.tsx`'s identical pattern, AD-005). Fully torn down by
 * `dispose()`; nothing here outlives that call.
 */
export function createWorldScene(container: HTMLDivElement, callbacks: WorldSceneCallbacks): WorldScene {
  const { scene, camera, renderer, controls, ground } = buildSceneObjects(container);
  const state: SceneState = {
    scene,
    camera,
    renderer,
    controls,
    container,
    ground,
    regionGroup: undefined,
    regionCache: new LruCache<string, CachedRegion>(REGION_CACHE_CAPACITY, (entry) => disposeTree(entry.group)),
    origin: { x: 0, z: 0 },
    disposed: false,
    pendingInit: Promise.resolve(),
    regionRequestGuard: new LatestRequestGuard(),
    drawCallCount: 0,
  };
  state.pendingInit = watchInit(state, callbacks);

  const resizeObserver = new ResizeObserver(() => resize(state));
  resizeObserver.observe(container);

  return {
    setRegion: async (region) => {
      await applyRegion(state, region).catch((error: unknown) => {
        callbacks.onStatus("error", error instanceof Error ? error.message : String(error));
      });
    },
    focusPlacement: (placement) => {
      if (!placement) return;
      const transform = placementWorldTransform(placement, state.origin);
      state.controls.target.set(transform.position.x, transform.position.y, transform.position.z);
      state.camera.position.set(
        transform.position.x + FOCUS_DISTANCE_M * 0.6,
        transform.position.y + FOCUS_DISTANCE_M * 0.45,
        transform.position.z + FOCUS_DISTANCE_M * 0.6,
      );
      state.controls.update();
    },
    drawCallCount: () => state.drawCallCount,
    dispose: () => {
      resizeObserver.disconnect();
      state.disposed = true;
      state.renderer.setAnimationLoop(null);
      detachRegion(state);
      // Only now do the resources that outlive individual regions go: every
      // retained region (`clear()` runs the cache's disposal hook for each)
      // and then the scene's own dressing.
      state.regionCache.clear();
      disposeTree(scene);
      controls.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      void state.pendingInit.finally(() => renderer.dispose());
    },
  };
}
